import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link, Redirect } from 'react-router-dom';
import moment from 'moment';
import {
  Layout, Table, Typography,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../configure/components/ConfigHeading';
import { selectPurchaseHistory } from '../../../state/domain/purchases/purchaseHistoryReducer';
import { selectCreditRanges, selectFetchMyUsageSummariesRequest } from '../../../state/ui/configuration/packages/packageUiReducer';
import { requestCreditUsageHistory, requestMyUsageSummaries } from '../../../state/ui/configuration/packages/packageUiActions';
import { entryRoutes } from '../../../utility/route-locations';
import { selectPackage } from '../../../state/domain/packages/packageReducer';

const { Text } = Typography;
const { Column } = Table;

function UsageTable({
  isLoading, creditUsages,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${creditUsages.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  const [visibleCreditUsages, setVisibleCreditUsages] = useState([]);
  useEffect(() => {
    const tempCreditUsages = creditUsages.slice();
    setVisibleCreditUsages(tempCreditUsages.map(creditUsage => ({
      ...creditUsage,
      consumedDateTimeDisplayable: moment.utc(creditUsage.consumedDateTime).format('dddd, MMMM Do YYYY, h:mm:ss a'),
    })));
  }, [creditUsages]);

  function sortByDateTime(a, b) {
    const aDate = moment.utc(a.consumedDateTime);
    const bDate = moment.utc(b.consumedDateTime);
    if (aDate.isAfter(bDate)) return 1;
    if (aDate.isBefore(bDate)) return -1;
    return 0;
  }

  function sortUsersByUsername(a, b) {
    const aStr = `${a.usedByUsername}`;
    const bStr = `${b.usedByUsername}`;
    return aStr.localeCompare(bStr);
  }
  // TABLE SETTINGS, EXPANDED ROW, COLUMNS
  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={visibleCreditUsages}
      rowKey={creditUsage => creditUsage.key}
    >
      <Column
        title={<FormattedMessage id="inspectionReference" />}
        dataIndex="inspectionId"
        key="inspectionId"
      />
      <Column
        sorter={sortUsersByUsername}
        title={<FormattedMessage id="inspectorEmail" />}
        dataIndex="usedByUsername"
        key="usedByUsername"
      />
      <Column
        title={<FormattedMessage id="yourReference" />}
        dataIndex="inspectionReference"
        key="inspectionReference"
      />
      <Column
        title={<FormattedMessage id="regNumber" />}
        dataIndex="vehicleReg"
        key="vehicleReg"
      />
      <Column
        sorter={sortByDateTime}
        title={<FormattedMessage id="utilisationDateTime" />}
        defaultSortOrder="descend"
        dataIndex="consumedDateTimeDisplayable"
        key="consumedDateTimeDisplayable"
      />
    </Table>
  );
}

UsageTable.propTypes = {
  isLoading: PropTypes.bool,
  creditUsages: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number.isRequired,
      inspectionId: PropTypes.number.isRequired,
      usedByUsername: PropTypes.string.isRequired,
      inspectionReference: PropTypes.string,
      vehicleReg: PropTypes.string.isRequired,
      consumedDateTime: PropTypes.string.isRequired,
    }),
  ),
};

UsageTable.defaultProps = {
  isLoading: false,
  creditUsages: [],
};

/**
 * Displays the details of the package.
 */
function CurrentDetails({ currentPackage }) {
  if (!currentPackage) {
    return (
      <></>
    );
  }
  return (
    <div className="credit-enquiry--user-details">
      <div className="user-result--keywords">
        <div>
          <span>
            <b>
              <FormattedMessage id="credits" />
            </b>
            :&nbsp;
          </span>
          <span>
            {currentPackage.mainTokens}
          </span>
        </div>
        <div>
          <span>
            <b>
              <FormattedMessage id="bonusCredits" />
            </b>
            :&nbsp;
          </span>
          <span>
            {currentPackage.extraTokens}
          </span>
        </div>
      </div>
    </div>
  );
}

CurrentDetails.propTypes = {
  currentPackage: PropTypes.shape({
    price: PropTypes.number.isRequired,
    currencyCode: PropTypes.string.isRequired,
    mainTokens: PropTypes.number.isRequired,
    extraTokens: PropTypes.number.isRequired,
  }),
};

CurrentDetails.defaultProps = {
  currentPackage: null,
};

/**
 * A component allowing a bynx admin user to retrive the purchase history for a package.
 */
function CreditHistory({
  isLoading, loadPageData, packageDateRangeMap, match, getPackage, loadMyUsageSummaries,
}) {
  const { id } = match.params;


  const pagePackage = getPackage(id);
  useEffect(() => {
    if (!pagePackage || !pagePackage.title) {
      loadMyUsageSummaries(moment.utc().toISOString(), moment.utc().toISOString());
    }
    if (pagePackage && !pagePackage.creditUsages) {
      loadPageData(id, packageDateRangeMap[id]);
    }
  }, [loadPageData, id, packageDateRangeMap, pagePackage, loadMyUsageSummaries]);

  const title = pagePackage ? pagePackage.title : null;
  if (!packageDateRangeMap[id]) {
    return <Redirect to={entryRoutes.creditUsage} />;
  }
  return (
    <Layout className="via-centre via-layout-configure-main">
      <ConfigHeading
        titleId="loading"
        descriptionId="creditHistoryDesc"
        border="none"
        title={title}
        links={[(
          <Link to={entryRoutes.creditUsage}>
            <FormattedMessage id="returnToCreditSummary" />
          </Link>
        )]}
      >
        <CurrentDetails currentPackage={pagePackage} />
        <UsageTable
          isLoading={isLoading}
          creditUsages={pagePackage ? pagePackage.creditUsages : []}
          includeCompanyName
        />
      </ConfigHeading>
    </Layout>
  );
}

// Validate the prop type parameters.
CreditHistory.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  packageDateRangeMap: PropTypes.objectOf(
    PropTypes.shape({
      toDate: PropTypes.string,
      fromDate: PropTypes.string,
    }),
  ).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  getPackage: PropTypes.func.isRequired,
  loadMyUsageSummaries: PropTypes.func.isRequired,
};

CreditHistory.defaultProps = {
  isLoading: false,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectFetchMyUsageSummariesRequest(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    purchases: selectPurchaseHistory(state),
    packageDateRangeMap: selectCreditRanges(state),
    getPackage: id => selectPackage(state, id),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (packageId, packageDateRange) => dispatch(requestCreditUsageHistory(
    packageId, packageDateRange.fromDateString, packageDateRange.toDateString,
  )),
  loadMyUsageSummaries: (fromDate, toDate) => dispatch(requestMyUsageSummaries(fromDate, toDate)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(CreditHistory);
